import React, { useEffect, useState } from "react";
import studioData from "../data/studioData.json";

const Header = () => {
  const { title, address } = studioData.studioProfile;
  const headerContent = studioData.siteContent?.header ?? {};
  const navItems = Array.isArray(headerContent.navItems) ? headerContent.navItems : [
    { id: "studio", label: "Studio" },
    { id: "services", label: "Services" },
    { id: "interior-packages", label: "Packages" },
    { id: "credentials", label: "Credentials" },
    { id: "gallery", label: "Gallery" },
    { id: "contact", label: "Contact" },
  ];
  const [isHeaderShrunk, setIsHeaderShrunk] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const SHRINK_THRESHOLD = 40;
    const EXPAND_THRESHOLD = 16;

    const handleScroll = () => {
      const currentY = window.scrollY;
      setIsHeaderShrunk((prev) => {
        if (!prev && currentY > SHRINK_THRESHOLD) {
          return true;
        }
        if (prev && currentY < EXPAND_THRESHOLD) {
          return false;
        }
        return prev;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <div className="h-20 w-full md:h-24">
        <header
          className={`glass-panel fixed left-0 right-0 top-0 z-50 border-x-0 border-t-0 pr-6 transition-all duration-300 ${isHeaderShrunk ? "h-14 md:h-16" : "h-20 md:h-24"
            }`}
        >
          <div className="mx-auto flex h-full w-full max-w-6xl items-center justify-between gap-4">
            <a href="#main-content" className="ml-4 md:ml-0 xl:-ml-6 inline-flex items-center gap-2 md:gap-4" aria-label="Skip to content">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo/logo.png`}
                alt="Blackpod logo"
                className={`${isHeaderShrunk ? "h-10 w-10 md:h-12 md:w-12" : "h-12 w-12 md:h-16 md:w-16"} object-contain transition-all duration-300 ease-out`}
              />
              <div>
                <p
                  className={`font-display ${isHeaderShrunk ? "text-xl md:text-3xl" : "text-3xl md:text-4xl"} leading-none text-white tracking-[-0.01em] transition-all duration-300 ease-out`}
                  aria-label="blackpod.art"
                >
                  <span className="font-light">black</span><span className="font-extrabold">pod</span><span className="font-light">.art</span>
                </p>
                {!isHeaderShrunk && (<p className={`text-[12px] md:text-[10px]uppercase tracking-[0.16em] md:tracking-[0.22em] text-text-secondary transition-all duration-300 ease-out`}>{title}</p>)}
                
              </div>
            </a>

            <nav className="header-menu hidden items-center justify-end lg:flex" aria-label="Primary navigation">
              {navItems.map((item) => (
                <a key={item.id} className="glass-nav-item header-menu-item" href={`#${item.id}`}>
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="ml-auto flex lg:hidden">
              <button
                type="button"
                className="glass-nav-item"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-nav"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              >
                {isMobileMenuOpen ? "Close" : "Menu"}
              </button>
            </div>
          </div>
        </header>

        {isMobileMenuOpen && (
          <nav
            id="mobile-nav"
            className="glass-nav-panel glass-pop fixed right-6 top-[5.5rem] z-40 flex w-52 flex-col gap-2 p-3 lg:hidden"
            aria-label="Mobile navigation"
          >
            {navItems.map((item) => (
              <a key={item.id} className="glass-nav-item" href={`#${item.id}`} onClick={handleNavClick}>
                {item.label}
              </a>
            ))}
          </nav>
        )}
      </div>

      <section className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-12 pt-12 md:pb-16 md:pt-16">
        <div className="glass-panel overflow-hidden p-8 md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
            <div className="space-y-6">
              <p className="section-kicker">
                {headerContent.heroKicker ?? "Architecture and Interiors"}
              </p>
              <h1 className="hero-title uppercase">
                {headerContent.heroTitle ??
                  "Spaces composed through light, proportion, and deliberate contrast."}
              </h1>
              <div className="flex flex-wrap gap-3">
                <a className="glass-button" href="#gallery">
                  {headerContent.primaryCtaLabel ?? "View Gallery"}
                </a>
                <a className="outline-link" href="#contact">
                  {headerContent.secondaryCtaLabel ?? "Start a Project"}
                </a>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-text-secondary md:grid-cols-2 lg:grid-cols-1">
              <div className="glass-card p-4">
                <p className="section-kicker !mb-2">{headerContent.locationLabel ?? "Location"}</p>
                <p className="text-base text-white">{address}</p>
              </div>
              <div className="glass-card p-4">
                <p className="section-kicker !mb-2">{headerContent.focusLabel ?? "Focus"}</p>
                <p className="text-base text-white">
                  {headerContent.focusValue ?? "Residential, Hospitality, Boutique Commercial"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Header;
